/* eslint-disable jsx-a11y/anchor-is-valid */
import DoneAllIcon from '@mui/icons-material/DoneAll';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import {
	Badge,
	Box,
	Divider,
	IconButton,
	List,
	ListItem,
	ListItemText,
	Skeleton
} from '@mui/material';
import { formatDistance } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LocalStorageKey } from '../../../../../../../constants/localStorageKeys';
import { DateHelper } from '../../../../../../../helpers/dateHelper';
import { buildLocalizeRoute } from '../../../../../../../helpers/languageHelper';
import { LanguageService } from '../../../../../../../services/languageService';
import { NotificationsService } from '../../../../../../../services/notificationsService';
import { ArrowIcons } from '../../../../../../../shared/components/common/arrowIcons';
import { TextWrapper } from '../../../../../../components/formUtilities/useTextWrapper';
import MenuPopover from '../../../../borrowerLayout/shared/components/menuPopover';

export const Notification = {
	All: 'all',
	UnRead: 'unRead',
};

const eventLists = [
	{
		name: 'DepositApproved',
		actionUrl: buildLocalizeRoute(
			'investor/transfers/deposit?isNotificationTransaction=true'
		),
	},
	{
		name: 'DepositRejected',
		actionUrl: buildLocalizeRoute(
			'investor/transfers/deposit?isNotificationTransaction=true'
		),
	},
	{
		name: 'WithdrawalApproved',
		actionUrl: buildLocalizeRoute(
			'investor/transfers/withdraw?isNotificationTransaction=true'
		),
	},
	{
		name: 'WithdrawalRejected',
		actionUrl: buildLocalizeRoute(
			'investor/transfers/withdraw?isNotificationTransaction=true'
		),
	},
	{
		name: 'NewLoanNote',
		actionUrl: buildLocalizeRoute(
			'investor/my-portfolio?isNotificationComment=true'
		),
	},
];

const NotificationType = {
	Operations: 1,
	Compliance: 2,
	Credit: 3,
};

export default function NotificationsPopover() {
	const languageService = new LanguageService();
	const notificationsService = new NotificationsService();
	const anchorRef = useRef(null);
	const navigate = useNavigate();
	const [allRead, setAllRead] = useState(false);
	const [loading, setLoading] = useState(true);
	const [allCount, setAllCount] = useState(0);
	const [unReadCount, setunReadCount] = useState(0);
	const [notifications, setNotifications] = useState([]);

	const [open, setOpen] = useState(null);
	const { t } = useTranslation();

	const handleOpen = (event, type) => {
		setOpen(event.currentTarget);
	};

	const handleClose = () => {
		setOpen(null);
		setTimeout(() => {
			localStorage.setItem(
				LocalStorageKey.notificationType,
				Notification.UnRead
			);
			setNotifications([]);
		}, 400);
	};

	const isAr = () => {
		return languageService.isArabic();
	};

	useEffect(() => {
		getCount();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getCount = () => {
		getUnReadCount();
		getAllCount();
	};

	const getUnReadCount = () => {
		notificationsService
			.getUnReadMessages()
			.then((resp) => {
				setunReadCount(resp.data?.length || 0);
			})
			.catch((err) => { });
	};

	const getAllCount = () => {
		notificationsService
			.getAllMessages()
			.then((resp) => {
				setAllCount(resp.data?.length || 0);
			})
			.catch((err) => { });
	};

	useEffect(() => {
		if (open) {
			getNotifications();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	useEffect(() => {
		const ids = notifications
			.filter((resp) => !resp.seen)
			.map((data) => {
				return data?.id;
			});
		if (ids?.length) {
			setAllRead(true);
		} else {
			setAllRead(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [notifications]);

	const getNotificationType = () => {
		return (
			localStorage.getItem(LocalStorageKey.notificationType) ||
			Notification.UnRead
		);
	};

	const getNotifications = () => {
		setLoading(true);
		if (getNotificationType() === Notification.UnRead) {
			getUnReadNotifications();
		} else {
			getAllNotifications();
		}
	};

	const getAllNotifications = () => {
		notificationsService
			.getAllMessages()
			.then((resp) => {
				setNotifications(resp.data);
				setLoading(false);
			})
			.catch((err) => {
				setLoading(false);
			});
	};

	const getUnReadNotifications = () => {
		notificationsService
			.getUnReadMessages()
			.then((resp) => {
				setNotifications(resp.data);
				setLoading(false);
			})
			.catch((err) => {
				setLoading(false);
			});
	};

	const changeTab = (type) => {
		setNotifications([]);
		localStorage.setItem(LocalStorageKey.notificationType, type);
		setTimeout(() => {
			getNotifications();
		});
	};

	const onEventClick = (notification) => {
		let actionUrl = eventLists.find(
			(resp) => resp.name === notification?.additionalData?.key
		).actionUrl || null;
		handleClose();
		markAsRead(notification);
		if (actionUrl) {
			navigate(actionUrl);
		}
	};

	const markAsRead = (notification) => {
		if (!notification?.seen) {
			let form = {
				id: notification?.id,
				isRead: true
			};
			notificationsService
				.markAsReadMessage(form)
				.then((resp) => {
					if (unReadCount > 0) {
						setunReadCount(unReadCount - 1);
					}
				})
				.catch((err) => {
					setLoading(false);
				});
		}
	};

	const markAsAllRead = () => {
		notificationsService
			.markAsAllReadMessage()
			.then((resp) => {
				setunReadCount(0);
				let currentNotifications = notifications || [];
				currentNotifications?.forEach((resp) => {
					resp.seen = true;
				});
				setNotifications([...currentNotifications]);
			})
			.catch((err) => {
				setLoading(false);
			});
	};

	const getLoaderSkeleton = () => {
		return [1, 2, 3, 4].map((value, index) => (
			<Box className="mt-2" key={index}>
				<ListItem
					disablePadding
					key={index}
					className={`hover:bg-gray-100 cursor-pointer !px-4`}>
					<ListItemText
						primaryTypographyProps={{ fontSize: '14px' }}
						secondaryTypographyProps={{ fontSize: '12px' }}
						primary={
							<Box className="flex justify-between items-center">
								<span className="font-[500]">
									<Skeleton
										variant="text"
										sx={{ fontSize: '10px', width: '120px' }}
									/>
								</span>
								<Box className="min-w-[100px] flex justify-end mb-2">
									<Skeleton
										variant="circular"
										sx={{ width: '30px', height: '30px' }}
									/>
								</Box>
							</Box>
						}
						secondary={
							<Box className="flex justify-between">
								<span>
									<Skeleton
										variant="text"
										sx={{ fontSize: '7px', width: '80px' }}
									/>
								</span>
								<span>
									<Skeleton
										variant="text"
										sx={{ fontSize: '7px', width: '60px' }}
									/>
								</span>
							</Box>
						}
					/>
				</ListItem>
				<Divider></Divider>
			</Box>
		));
	};

	return (
		<>
			<IconButton
				className="!p-0 !mx-3"
				ref={anchorRef}
				color={open ? 'primary' : 'default'}
				onClick={(event) => {
					handleOpen(event, NotificationType.Operations);
				}}
				sx={{ width: 40, height: 40 }}>
				<Badge
					badgeContent={unReadCount}
					showZero={true}
					color="error">
					<NotificationsNoneOutlinedIcon className="!text-white" />
				</Badge>
			</IconButton>

			<MenuPopover
				open={Boolean(open)}
				anchorEl={open}
				onClose={handleClose}
				sx={{ width: 350, p: 0, mt: 1.5, ml: 0.75 }}
				className={`notification-popup ${isAr() ? 'notification-popup-ar' : ''
					}`}>
				<Box className="flex flex-col">
					<Box className="p-3 text-center border-b w-full">
						<TextWrapper
							classList="!font-[500] !text-[18px]"
							text={t('notifications')}></TextWrapper>
					</Box>
					<Box className="px-4 pb-2 pt-3">
						<Box className="flex justify-start items-center w-full bg-[#f9f9f9] p-1 rounded-[4px]">
							<Box
								onClick={() => {
									changeTab(Notification.UnRead);
								}}
								className={`notification-tab ${getNotificationType() === Notification.UnRead
									? 'active-tab'
									: ''
									}`}>
								<span>{t('unRead')}</span>
								<span className="mx-1">({unReadCount})</span>
							</Box>
							<Box className="w-4"></Box>
							<Box
								onClick={() => {
									changeTab(Notification.All);
								}}
								className={`notification-tab ${getNotificationType() === Notification.All ? 'active-tab' : ''
									}`}>
								<span>{t('all')}</span>
								<span className="mx-1">({allCount})</span>
							</Box>
						</Box>
					</Box>
					<List
						className="!p-0"
						sx={{
							width: '100%',
							maxWidth: 400,
							bgcolor: 'background.paper',
						}}>
						{loading ? (
							getLoaderSkeleton()
						) : notifications?.length > 0 ? (
							<>
								{notifications.map((value, index) => (
									<Box>
										<ListItem
											onClick={() => {
												onEventClick(value);
											}}
											disablePadding
											key={index}
											className={`hover:bg-gray-100 ${value?.seen ? 'opacity-50 hover:opacity-100' : ''} cursor-pointer !px-4 min-h-[78px] notification-content ${index === 0 ? 'border-t' : ''
												}`}>
											<ListItemText
												primaryTypographyProps={{ fontSize: '14px' }}
												secondaryTypographyProps={{ fontSize: '12px' }}
												primary={
													<Box className="flex justify-between items-center">
														<Box>
															<Box className="font-[500]">
																{value?.title}
															</Box>
															<Box className="text-[12px] caption">
																{value?.body}
															</Box>
														</Box>
														<ArrowIcons
															classList={`!text-[#5d5d5d] notification-arrow !text-[17px] ${isAr() ? '!mr-2' : '!ml-2'
																}`}
															isKeyboardArrow></ArrowIcons>
													</Box>
												}
												secondary={
													<Box className="flex justify-between mt-1">
														<span>
															{DateHelper.formatDate(
																value?.createDate,
																'MMM dd, yyyy hh:mm'
															)}
														</span>
														<span dir="ltr">
															{formatDistance(
																new Date(value?.createDate),
																new Date(),
																{
																	addSuffix: true,
																}
															)?.replace('about ', '')}
														</span>
													</Box>
												}
											/>
										</ListItem>
										<Divider></Divider>
									</Box>
								))}
							</>
						) : (
							<Box className="min-h-[200px] flex flex-col items-center justify-center">
								<NotificationsNoneOutlinedIcon
									color="primary"
									className="md:!text-[70px] !text-[50px]"
								/>
								<Box className="mt-3 font-[500] !text-[16px]">
									{t('noNotificationsYet')}
								</Box>
								<TextWrapper
									type="caption"
									text={t('youHaveNoNotifications')}></TextWrapper>
							</Box>
						)}
					</List>
					<Box className="flex justify-center p-5">
						{allRead && !loading ? (
							<a
								onClick={() => {
									markAsAllRead();
								}}
								className="underline-link flex items-center">
								<DoneAllIcon className="!text-[18px]" />
								<span className="mx-1">{t('markAllAsRead')}</span>
							</a>
						) : null}
					</Box>
				</Box>
			</MenuPopover>
		</>
	);
}
